import { useState, useEffect } from "react";
import DataTable from "../../utils/components/DataTable";
import Breadcrumbs from "../../utils/components/Breadcrumbs";
import Button from "../../utils/components/Button";
import NavigationTabs from "../../utils/components/NavigationTabs";
import Modal from "../../utils/components/Modal";
import { useLocation, useNavigate } from "react-router";
import { auth } from "../../App/routes";
import { useProject } from "../../utils/hooks/useProject";
import API from "../../utils/API";
import timeStampToDateFormat from "../../utils/helpers/timestampToDateFormat";
import { Translate } from "react-localize-redux";
import { useCan } from "../../utils/hooks/useCan";
import Loading from "../../utils/components/Loading";

const statusKeys = {
  inprogress: "in-progress",
  completed: "completed",
};

const workflowsColumns = [
  {
    name: "ID",
    selector: (row) => row.id,
    sortable: true,
    maxWidth: "100px",
  },
  {
    name: (
      <Translate id="design_approval_process.uppercase_translates.form-no" />
    ),
    selector: (row) => row.form_no,
    sortable: true,
    maxWidth: "130px",
  },
  {
    name: (
      <Translate id="design_approval_process.uppercase_translates.workflow-template-name" />
    ),
    selector: (row) => row.name,
    sortable: true,
    minWidth: "200px",
  },
  {
    name: (
      <Translate id="design_approval_process.uppercase_translates.status" />
    ),
    selector: (row) => (
      <Translate
        id={`design_approval_process.uppercase_translates.statuses.${
          statusKeys[row.status]
        }`}
      />
    ),
    sortable: true,
  },
  {
    name: <Translate id="design_approval_process.uppercase_translates.step" />,
    selector: (row) => row.active_steps,
    sortable: true,
  },
  {
    name: (
      <Translate id="design_approval_process.uppercase_translates.start-date" />
    ),
    selector: (row) => row.start_date,
    format: (row) => timeStampToDateFormat(row.start_date),
    sortable: true,
  },
  {
    name: (
      <Translate id="design_approval_process.uppercase_translates.end-date" />
    ),
    selector: (row) => row.end_date,
    format: (row) => timeStampToDateFormat(row.end_date),
    sortable: true,
  },
  {
    name: (
      <Translate id="design_approval_process.uppercase_translates.started-by" />
    ),
    selector: (row) => row.started_by,
    sortable: true,
  },
  {
    name: (
      <Translate id="design_approval_process.uppercase_translates.updated-at" />
    ),
    selector: (row) =>
      row.updated_at ? timeStampToDateFormat(row.updated_at) : "-",
    sortable: true,
  },
];

const workflowTemplatesColumns = [
  {
    name: "ID",
    selector: (row) => row.id,
    sortable: true,
    maxWidth: "100px",
  },
  {
    name: <Translate id="design_approval_process.uppercase_translates.name" />,
    selector: (row) => row.name,
    sortable: true,
    minWidth: "300px",
  },
  {
    name: (
      <Translate id="design_approval_process.uppercase_translates.serie-count" />
    ),
    selector: (row) => row.serie_count,
    sortable: true,
  },
  {
    name: (
      <Translate id="design_approval_process.uppercase_translates.status" />
    ),
    selector: (row) => row.status,
    sortable: true,
  },
  {
    name: (
      <Translate id="design_approval_process.uppercase_translates.created-at" />
    ),
    selector: (row) => row.created_at,
    format: (row) => timeStampToDateFormat(row.created_at),
    sortable: true,
  },
  {
    name: (
      <Translate id="design_approval_process.uppercase_translates.updated-at" />
    ),
    selector: (row) => row.update_at,
    format: (row) =>
      row.update_at ? timeStampToDateFormat(row.update_at) : "-",
    sortable: true,
  },
];

const TABS = [
  {
    id: 0,
    key: "design_approval_process.workflows",
    permissionRequired: false,
    dataKey: "workflowsList",
    columns: workflowsColumns,
    linkPrefix: "w-",
    endpoint: "workflow",
  },
  {
    id: 1,
    key: "design_approval_process.workflow-templates",
    permissionRequired: { read: "workflow:read", create: "workflow:create" },
    dataKey: "workflowTemplatesList",
    columns: workflowTemplatesColumns,
    linkPrefix: "",
    endpoint: "workflow_templates",
  },
];

const DesignApprovalProcess = ({ translate }) => {
  const [data, setData] = useState({
    workflowsList: [],
    workflowTemplatesList: [],
  });
  const [isProjectControl, setIsProjectControl] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const {state} =useLocation()
  const { project } = useProject();
  const [isFetched, setIsFetched] = useState({
    workflowsList: false,
    workflowTemplatesList: false,
  });

  const can = useCan();

  const handleTabChange = (tabId) => {
    setIsLoading(true);
    const tab = TABS.find((tab) => tab?.id === tabId);

    API.getData(`${tab.endpoint}?project_id=${project.id}`).then(
      ({ data: res }) => {
        setData((prevState) => ({ ...prevState, [tab.dataKey]: res.data }));
        setIsLoading(false);
        setIsFetched((prevState) => ({
          ...prevState,
          [tab.dataKey]: true,
        }));
      }
    );
  };

  useEffect(() => {
    if (project) {
      setIsLoading(true);
      const tab = TABS[activeTab];
      if (!tab) return;

      if (
        !isFetched[tab.dataKey] &&
        ((tab.permissionRequired && can.doThat(tab.permissionRequired?.read)) ||
          !tab.permissionRequired)
      )
        handleTabChange(tab.id);
      else setTimeout(() => setIsLoading(false), 200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (project) {
      setIsProjectControl(false);
    } else {
      setIsLoading(false);
      setIsProjectControl(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const tabKey = state?.activeTab;
    if(!tabKey) return;

    const tab = TABS.find((tab) => tab.dataKey === tabKey);
    setActiveTab(tab?.id);
  }, [state]);

  return (
    <div className="container-fluid mb-5">
      <div className="row my-4">
        <div className="col-12 col-md-6">
          <Breadcrumbs
            data={[
              {
                id: 0,
                customTitle: project?.name || translate("projects.title"),
                route: "auth.projects",
              },
              { id: 1, title: "page_titles.workflows" },
            ]}
          />
        </div>
        {TABS[activeTab]?.permissionRequired?.create &&
          can.doThat(TABS[activeTab].permissionRequired?.create) && (
            <div className="col-12 col-md-6 text-end">
              {project && (
                <Button
                  className="btn-orange text-white px-4"
                  onClick={() =>
                    navigate(
                      auth.designApprovalProcessDetail.link.replace(
                        ":id",
                        "new"
                      )
                    )
                  }
                >
                  {translate("design_approval_process.new-workflow-template")}
                </Button>
              )}
            </div>
          )}
      </div>
      <div className="bg-white py-2 shadow-sm border border-1">
        <div className="col-12 border-bottom border-1">
          <NavigationTabs
            tabs={
              TABS.filter((tab) => {
                if (tab.permissionRequired) {
                  return can.doThat(tab.permissionRequired?.read);
                }
                return true;
              })?.map((tab) => ({
                id: tab.id,
                label: translate(tab.key),
              })) || []
            }
            activeTab={TABS[activeTab]?.id}
            onTabClick={(tab) => {
              setActiveTab(TABS.findIndex((t) => t.id === tab));
            }}
          />
        </div>
        <div className="col-12">
          <DataTable
            columns={TABS[activeTab]?.columns}
            data={data[TABS[activeTab]?.dataKey]}
            onRowClicked={(row) => {
              navigate(
                auth.designApprovalProcessDetail.link.replace(
                  ":id",
                  TABS[activeTab]?.linkPrefix + row.id
                )
              );
            }}
            pointerOnHover
            progressComponent={
              <Loading classNames="mt-3" text={translate("loading") + `...`} />
            }
            progressPending={isLoading}
          />
        </div>
      </div>

      <Modal
        title={translate("alert")}
        modaSize="lg"
        show={isProjectControl && !isLoading}
        setShow={setIsProjectControl}
      >
        <p className="fs-5 text-center mt-2">
          {translate("select_a_project_alert")}
        </p>
      </Modal>
    </div>
  );
};

export default DesignApprovalProcess;
